import { useState } from 'react';
import _ from 'lodash';
import SearchableMenu from 'components/SearchableMenu';
import { Flex, Margin } from 'components/base/layout-components';
import Icon from 'components/base/Icon';
import { TRACKING } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import useRightScreenOverflow from './hooks/useRightScreenOverflow';
import { LBButton } from '@linearb/buzz';

const getFilterTypeElement = ({ documentIdPrefix }) =>
  ['opener', ...(documentIdPrefix ? [documentIdPrefix] : [])].join('-');

const MultiSearchableMenu = ({
  buttonTitle = 'Add',
  buttonIcon = 'Add',
  menus,
  searchFilterValue,
  setSearchFilterValue,
  AddFilterTrackingProps,
  documentIdPrefix = null,
  isLoading = false,
  onClearFilters,
  btnClearFiltersTitle,
  withMargin = true,
  viewOnly = false,
  autoResponsive,
  onCloseMenu = _.noop,
  triggerMenuButtonRef = null,
  isSingleFilterMode,
  onClickTriggerButton = _.noop
}) => {
  const [anchorEl, setAnchorEl] = useState({});
  const [levelOpened, setLevelOpened] = useState(0);
  const rightScreenOverflow = useRightScreenOverflow({
    anchorEl
  });

  const defaultMenuWidth = '20rem';
  const filterTypeElement = document.getElementById(
    getFilterTypeElement({ documentIdPrefix })
  );

  const formatMenuItems = (menuItems = [], level) =>
    menuItems.map(
      ({
        isClickToOpenNextMenu = false,
        isFreeInputs = false,
        disabled,
        onClick = _.noop,
        ...menuItem
      }) => ({
        ...menuItem,
        disabled,
        onClick: props => {
          if (disabled) return;
          if (isFreeInputs) {
            handleMenuOpenFreeInput();
          }
          if (isClickToOpenNextMenu) {
            handleMenuOpen(level);
          }
          onClick(props);
        }
      })
    );

  const formatMenuActionButtons = (actions = [], level) => {
    const buttons = actions.map(
      (
        {
          title,
          isClickToOpenNextMenu = false,
          isClickToCloseMenu = false,
          isClickToCloseAllMenus = false,
          buttonProps = {},
          onClick = _.noop,
          dataCy
        },
        index
      ) => {
        const button = (
          <LBButton
            {...(dataCy ? { 'data-cy': dataCy } : {})}
            key={index}
            variant={buttonProps.variant || 'outline'}
            size={buttonProps.size || 'default'}
            {...(buttonProps.startIcon
              ? { startIcon: buttonProps.startIcon }
              : {})}
            {...(buttonProps.endIcon ? { endIcon: buttonProps.endIcon } : {})}
            onClick={() => {
              if (isClickToOpenNextMenu) {
                handleMenuOpen(level);
              }
              if (isClickToCloseMenu) {
                handleMenuClose(level);
              }
              if (isClickToCloseAllMenus) {
                handleMenusClose();
              }
              onClick();
            }}
          >
            {title}
          </LBButton>
        );

        return index > 0 ? (
          <Margin l='x-small' key={index}>
            {button}
          </Margin>
        ) : (
          button
        );
      }
    );

    if (!buttons.length) return null;
    return <Flex justify='flex-end'>{buttons}</Flex>;
  };

  const handleMenuOpen = level => {
    const menusToOpen = {};
    menus
      .slice(0, level + 1)
      .forEach(({ id }) => (menusToOpen[id] = filterTypeElement));

    setLevelOpened(level + 1);
    setAnchorEl(menusToOpen);
  };

  const handleMenuClose = level => {
    const menusToOpen = {};
    menus
      .slice(0, level - 1)
      .forEach(({ id }) => (menusToOpen[id] = filterTypeElement));

    setLevelOpened(level - 1);
    setAnchorEl(menusToOpen);
    onCloseMenu(level);
  };

  const handleMenusClose = () => {
    setAnchorEl({});
    setLevelOpened(0);
    onCloseMenu(0);
  };

  const handleMenuOpenFreeInput = () => {
    const menusToOpen = {};
    [menus[0], menus[2]].forEach(
      ({ id }) => (menusToOpen[id] = filterTypeElement)
    );
    setLevelOpened(2);
    setAnchorEl(menusToOpen);
  };

  const isMenuOpenByAnchor = (menu, idx) =>
    Object.keys(anchorEl).includes(menu.id);

  if (!menus) return null;

  const openedMenus = menus.filter(isMenuOpenByAnchor);

  return (
    <div className='flex flex-row gap-1'>
      {!viewOnly && (
        <LBButton
          id={getFilterTypeElement({ documentIdPrefix })}
          ref={triggerMenuButtonRef}
          variant='outline'
          size='lg'
          data-testid='add-filters-button'
          data-cy='add-filters-button'
          onClick={e => {
            TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
              ...AddFilterTrackingProps
            });

            const menuToOpen = isSingleFilterMode ? 1 : 0;
            setAnchorEl({ [menus[menuToOpen].id]: e.currentTarget });
            setLevelOpened(1);
            onClickTriggerButton();
          }}
        >
          <Icon color='chambray' name={buttonIcon} /> {buttonTitle}
        </LBButton>
      )}
      {!!onClearFilters && (
        <LBButton
          variant='outline'
          size='lg'
          data-testid='clear-filters-button'
          onClick={onClearFilters}
        >
          <Icon color='chambray' name='Close' /> {btnClearFiltersTitle}
        </LBButton>
      )}

      {openedMenus.map(
        (
          {
            id,
            width = defaultMenuWidth,
            header,
            subheader,
            isSearchable,
            isMultiple,
            selectedCheckboxes,
            maxSelectedCheckboxes,
            actions,
            menuItems,
            freeChild,
            onSearch,
            noDataMessage,
            onScrollToBottom
          },
          index
        ) => {
          const level = parseInt(index + 1);

          const maxHeight = level === 1 ? '40.5rem' : '33rem';

          const menusWidth = menus
            .slice(0, index)
            .map(({ width = defaultMenuWidth }) => parseInt(width));

          const leftPosition = index
            ? `${_.sum(menusWidth) + index * 0.5}rem`
            : '0rem';
          const leftPositionWithOverflow = `calc(${leftPosition} - ${rightScreenOverflow}px)`;
          const isLoadingEntities =
            isLoading && (level === 2 || isSingleFilterMode);

          return (
            <SearchableMenu
              key={index}
              id={id}
              width={width}
              anchorEl={anchorEl[id]}
              handleMenusClose={() => handleMenuClose(level)}
              isLevel={level}
              leftPosition={
                autoResponsive ? leftPositionWithOverflow : leftPosition
              }
              isSearchable={isSearchable}
              isMultiple={isMultiple}
              isDisabled={levelOpened > level}
              header={header}
              subheader={subheader}
              selectedCheckboxes={selectedCheckboxes}
              maxSelectedCheckboxes={maxSelectedCheckboxes}
              actions={formatMenuActionButtons(actions, level)}
              menuItems={formatMenuItems(menuItems, level)}
              freeChild={freeChild}
              onSearch={onSearch}
              searchFilterValue={searchFilterValue}
              setSearchFilterValue={setSearchFilterValue}
              isLoading={isLoadingEntities}
              noDataMessage={noDataMessage}
              onScrollToBottom={onScrollToBottom}
              maxHeight={maxHeight}
            />
          );
        }
      )}
    </div>
  );
};

export default MultiSearchableMenu;
